import { signOut } from 'next-auth/react';
import { setMemoryToken } from '@/utils/tokenManager';
import { LogoutReason } from '../types';
import { resolveAuthContext } from '@/utils/auth-context';

function computeLoginUrl(): string {
    if (typeof window === 'undefined') return '/admin/login';

    const pathname = window.location.pathname;
    const context = resolveAuthContext(pathname);

    if (context === 'PROJECT_ADMIN') {
        const segments = pathname.split('/').filter(Boolean);
        const adminIndex = segments.indexOf('admin');
        if (adminIndex !== -1) {
            return `/${segments.slice(0, adminIndex + 1).join('/')}/login`;
        }
    }

    return '/admin/login';
}

export async function executeLogout(reason: LogoutReason = 'expired'): Promise<void> {
    if (typeof window === 'undefined') return;

    // Prevent multiple concurrent logout executions
    if ((window as any).__isLoggingOut) return;
    (window as any).__isLoggingOut = true;

    try {
        // 1. Clear memory token
        setMemoryToken(null);
        
        // 2. Clear local storages
        localStorage.removeItem('isLoggedIn');
        sessionStorage.clear();
        
        // 3. Dispatch event for UI toast (Admin/Heritage)
        window.dispatchEvent(new CustomEvent('session_expired_event'));
        
        const context = resolveAuthContext(window.location.pathname);
        if (context === 'HERITAGE' || context === 'PUBLIC') {
             // Heritage pages do not redirect, they just clear session
             await signOut({ redirect: false });
             (window as any).__isLoggingOut = false;
             return;
        }

        // 4. Compute login URL based on current context
        const loginUrl = computeLoginUrl();
        
        // 5. Delay before redirecting to allow toast to be visible
        setTimeout(async () => {
            await signOut({ redirect: false });
            (window as any).__isLoggingOut = false;
            // Add reason to URL for potential custom messages on login page
            window.location.href = `${loginUrl}?expired=true&reason=${reason}`;
        }, 2500);
    } catch (error) {
        console.error('[LogoutHandler] Error during logout execution:', error);
        (window as any).__isLoggingOut = false;
        
        const context = resolveAuthContext(window.location.pathname);
        if (context === 'HERITAGE' || context === 'PUBLIC') {
            return;
        }

        // Fallback force redirect - use same logic
        const fallbackUrl = computeLoginUrl();
        window.location.href = `${fallbackUrl}?expired=true`;
    }
}
